import get from 'lodash/get';
import merge from 'lodash/merge';
import {
    formatCamelCase,
    formatPascalCase,
    formatTitleCase,
} from './format-case';

const expressionsRegex = /\{\{([^}]+)\}\}/g;
const expressionRegex = /\{\{([^}]+)\}\}/;
const spaceSplitRegex = /(?:[^\s"]+|"[^"]*")+/g;
const pipeSplitRegex = /(?:[^>"]+|"[^"]*")+/g;
const isNumberRegex = /^(\d+(?:\.\d+)?)$/;
export function isTemplate(source: string) {
    return typeof source === 'string' && expressionRegex.test(source);
}

type TemplateToken = unknown;
type TemplateLocals = Record<string, unknown>;

function isValidDate(d: TemplateToken) {
    return d instanceof Date && !isNaN(Number(d));
}

export type TemplateHelperMap = {
    [key: string]: (locals: TemplateLocals, ...args: unknown[]) => unknown;
};

export const GET_ENV_PREFIX = 'FORM_CONFIG_';

export const TemplateDateMacros = {
    now: () => new Date(),
    yesterday: () => {
        const date = new Date();
        date.setDate(date.getDate() - 1);
        return date;
    },
    tomorrow: () => {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        return date;
    },
} as const;

function parseDateSource(source: TemplateToken) {
    if (
        typeof source !== 'string' &&
        typeof source !== 'number' &&
        !(source instanceof Date)
    )
        return new Date(0);

    return typeof source === 'string' && source in TemplateDateMacros
        ? TemplateDateMacros[source as keyof typeof TemplateDateMacros]()
        : new Date(
              String(source).match(isNumberRegex) ? Number(source) : source
          );
}

export const DefaultTemplateHelpers: TemplateHelperMap = {
    uppercase: (_locals, source) => String(source).toUpperCase(),
    lowercase: (_locals, source) => String(source).toLowerCase(),
    titlecase: (_locals, source) => formatTitleCase(String(source)),
    camelcase: (_locals, source) => formatCamelCase(String(source)),
    pascalcase: (_locals, source) => formatPascalCase(String(source)),
    number: (_locals, source, locale = 'en', maximumFractionDigits = 2) => {
        try {
            return new Intl.NumberFormat(String(locale), {
                maximumFractionDigits: Number(maximumFractionDigits),
            }).format(Number(source));
        } catch {
            console?.warn(`invalid expression ${source}`);
            return String(source);
        }
    },
    round: (_locals, source, locale = 'en') => {
        try {
            return new Intl.NumberFormat(String(locale)).format(
                Math.round(Number(source))
            );
        } catch {
            console?.warn(`invalid expression ${source}`);
            return String(source);
        }
    },
    floor: (_locals, source, locale = 'en') => {
        try {
            return new Intl.NumberFormat(String(locale)).format(
                Math.floor(Number(source))
            );
        } catch {
            console?.warn(`invalid expression ${source}`);
            return String(source);
        }
    },
    ceil: (_locals, source, locale = 'en') => {
        try {
            return new Intl.NumberFormat(String(locale)).format(
                Math.ceil(Number(source))
            );
        } catch {
            console?.warn(`invalid expression ${source}`);
            return String(source);
        }
    },
    currency: (_locals, source, locale = 'en', currency = 'EUR') => {
        try {
            return new Intl.NumberFormat(String(locale), {
                style: 'currency',
                currency: String(currency),
            }).format(Number(source));
        } catch {
            console?.warn(`invalid expression ${source}`);
            return String(source);
        }
    },
    date: (_locals, source, locale = 'en', dateStyle = 'medium') => {
        const date = parseDateSource(source);
        if (!isValidDate(date)) return source;
        try {
            return new Intl.DateTimeFormat(String(locale), {
                dateStyle: String(dateStyle) as
                    | 'full'
                    | 'long'
                    | 'medium'
                    | 'short'
                    | undefined,
            }).format(date);
        } catch {
            console?.warn(`invalid expression ${source}`);
            return String(source);
        }
    },
    trimToLength(_locals, source, length, ellipsis = '...') {
        const sourceString = String(source);
        const lengthNumber = Number(length);
        return sourceString.length > lengthNumber
            ? `${sourceString.substring(0, lengthNumber)}${ellipsis}`
            : sourceString;
    },
    readJson(_locals, source, path) {
        try {
            //workaround because the object is already stringified -> follow up needed for investigation why this happens
            const sourceString = JSON.parse(`"${source}"`);
            return this.get(JSON.parse(sourceString), path);
        } catch {
            console?.warn(`unable to parse string: ${source}`);
            return '';
        }
    },
    mergeJson(locals, ...sources) {
        if (sources.length < 2) {
            return sources[0];
        }

        // enforce sources to be interpreted as json data
        sources = sources.map((source) => {
            if (typeof source !== 'string') {
                return source;
            }

            try {
                return JSON.parse(source);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch {
                console.log('passed incompatible arguments to mergeJson');
                return undefined;
            }
        });

        // track if we are merging arrays or objects, first argument defines the type
        const sourceIsArray = Array.isArray(sources[0]);

        // keep it immutable
        let result = structuredClone(sources[0]);

        for (const mergeTarget of sources.slice(1)) {
            // ignore mismatching arguments
            if (Array.isArray(mergeTarget) !== sourceIsArray) {
                continue;
            }

            // simply concat arrays, deep merge objects
            if (sourceIsArray) {
                result = (result as unknown[]).concat(mergeTarget);
            } else {
                result = merge(result, mergeTarget);
            }
        }

        return this.json(locals, result);
    },
    datetime: (
        _locals,
        source,
        locale = 'en',
        dateStyle = 'medium',
        timeStyle = 'short'
    ) => {
        const date = parseDateSource(source);
        if (!isValidDate(date)) return source;
        try {
            return new Intl.DateTimeFormat(String(locale), {
                dateStyle: String(dateStyle) as
                    | 'full'
                    | 'long'
                    | 'medium'
                    | 'short'
                    | undefined,
                timeStyle: String(timeStyle) as
                    | 'full'
                    | 'long'
                    | 'medium'
                    | 'short'
                    | undefined,
            }).format(date);
        } catch {
            console?.warn(`invalid expression ${source}`);
            return source;
        }
    },
    time: (_locals, source, locale = 'en', timeStyle = 'short') => {
        const date = parseDateSource(source);
        if (!isValidDate(date)) return source;
        try {
            return new Intl.DateTimeFormat(String(locale), {
                timeStyle: String(timeStyle) as
                    | 'full'
                    | 'long'
                    | 'medium'
                    | 'short'
                    | undefined,
            }).format(date);
        } catch {
            console?.warn(`invalid expression ${source}`);
            return source;
        }
    },
    join: (_locals, delimiter = '', ...parts) => parts.join(String(delimiter)),
    concat: (_locals, ...parts) => parts.join(''),
    get: (locals, varname = '', ...segments) =>
        get(
            locals,
            [varname, ...segments].join(''),
            get(locals, String(varname), '')
        ),
    json: (_locals, value = '', space) =>
        JSON.stringify(
            value ?? null,
            undefined,
            space as string | number | undefined
        ),
    jsonString: (_locals, value = '', space) => {
        const result = JSON.stringify(
            value ?? null,
            undefined,
            space as string | number | undefined
        );

        if (result.startsWith('"') && result.endsWith('"'))
            return result.slice(1, -1);

        return result;
    },
    reverse: (_locals: TemplateLocals, varname = '') =>
        String(varname ?? '')
            .split('')
            .reverse()
            .join(''),
    echo: (_locals: TemplateLocals, string) => String(string ?? ''),
    condition: (locals, input, thenArg, elseArg) => {
        if (!input)
            return elseArg !== undefined
                ? get(locals, String(elseArg), elseArg)
                : '';

        let compare = false;
        try {
            compare = Boolean(
                JSON.parse(String(get(locals, String(input), String(input))))
            );
        } catch {
            compare = Boolean(String(input).trim());
        }
        if (compare) {
            return thenArg !== undefined
                ? get(locals, String(thenArg), thenArg)
                : '';
        }
        return elseArg !== undefined
            ? get(locals, String(elseArg), elseArg)
            : '';
    },
    flat: (_locals, input, depth = Infinity) => {
        if (typeof input === 'string') {
            try {
                input = JSON.parse(input);
            } catch {
                console?.warn('flat: failed to parse input');
                return null;
            }
        }

        if (!Array.isArray(input)) {
            console?.warn('flat: invalid input');
            return null;
        }

        return JSON.stringify(input.flat(Number(depth)));
    },
    getEnv: (_locals, varname = '', ...segments) => {
        const path = [varname, ...segments].join('');

        console.log('called with ', path);

        if (!path.startsWith(GET_ENV_PREFIX)) {
            console?.warn(
                `requested env variable "${path}" is not inside allowed prefix "${GET_ENV_PREFIX}"`
            );
            return '';
        }

        if (typeof process?.env !== 'object') {
            return '';
        }

        return get(process.env, path, '');
    },
};

function compileMatch(
    expression: string,
    locals: TemplateLocals = {},
    defaultValue?: string,
    helpers: TemplateHelperMap = DefaultTemplateHelpers,
    pipeResult?: unknown
) {
    let parts = Array.from(expression.match(spaceSplitRegex) ?? []);

    if (!parts.length) {
        if (pipeResult !== undefined) {
            return get(locals, pipeResult as string | string[], defaultValue);
        } else {
            return expression;
        }
    }

    if (pipeResult !== undefined) {
        const [helper, ...args] = parts;
        parts = [helper, JSON.stringify(pipeResult), ...args];
    }

    if (parts.length === 1) return get(locals, parts[0], defaultValue);

    if (parts[0] in helpers) {
        const [helperName, ...args] = parts;
        const helperArgs = args.map((part) => {
            const arg = part.trim();
            if (arg.startsWith('"') && arg.endsWith('"')) {
                return arg.slice(1, -1);
            } else if (arg.match(isNumberRegex)) {
                return Number(arg);
            }
            return get(locals, arg, defaultValue);
        });

        return helpers[helperName as keyof typeof helpers](
            locals,
            ...helperArgs
        );
    }

    return get(locals, parts.join(' '), defaultValue);
}

function compilePipes(
    expression: string,
    locals: TemplateLocals = {},
    defaultValue?: string,
    helpers: TemplateHelperMap = DefaultTemplateHelpers
) {
    const parts = Array.from(expression.match(pipeSplitRegex) ?? []).map(
        (part) => part.trim()
    );

    if (!parts.length) return expression;

    if (parts.length === 1)
        return compileMatch(parts[0], locals, defaultValue, helpers);

    let intermediateResult;

    for (const expression of parts) {
        intermediateResult = compileMatch(
            expression,
            locals,
            defaultValue,
            helpers,
            intermediateResult
        );
    }

    return intermediateResult;
}

export function compile(
    source: string,
    locals: TemplateLocals = {},
    defaultValue?: string,
    helpers: TemplateHelperMap = DefaultTemplateHelpers
) {
    const enhancedLocals = {
        ...locals,
        $this: locals,
    };

    return source.replace(expressionsRegex, (match) =>
        compilePipes(
            match.slice(2, -2).trim(),
            enhancedLocals,
            defaultValue,
            helpers
        )
    );
}
